import React, { useState, useEffect, useRef } from 'react';
import LoginPage from './LoginPage';
import './App.css';
import PDFCompare from './PDFCompare';

const SERVER_BASE_URL = process.env.REACT_APP_SERVER_BASE_URL || '';
const API_BASE = `${SERVER_BASE_URL}/api`;

const FIELDS = [
  { key: 'date',                    label: 'DATE',                    type: 'text' },
  { key: 'po_due_date',             label: 'PO DUE DATE',             type: 'text' },
  { key: 'release_no',              label: 'RELEASE #',               type: 'number', readonly: true },
  { key: 'trucker',                 label: 'TRUCKER',                 type: 'text' },
  { key: 'so_cs_no',                label: 'SO / CS NO',              type: 'text' },
  { key: 'po_no',                   label: 'PO NO',                   type: 'text' },
  { key: 'psd_updated',             label: 'PSD UPDATED',             type: 'text' },
  { key: 'pick_up_date',            label: 'PICK UP DATE',            type: 'text' },
  { key: 'scheduled_date',          label: 'SCHEDULED DATE',          type: 'text' },
  { key: 'estimated_delivery_date', label: 'ESTIMATED DELIVERY DATE', type: 'text' },
  { key: 'delivery_date',           label: 'DELIVERY DATE',           type: 'text' },
  { key: 'comments',                label: 'COMMENTS',                type: 'textarea' },
  { key: 'total_coils',             label: 'TOTAL COILS',             type: 'number' },
  { key: 'pickup_location',         label: 'PICKUP LOCATION',         type: 'text' },
  { key: 'destination_location',    label: 'DESTINATION LOCATION',    type: 'text' },
  { key: 'total_weight',            label: 'TOTAL WEIGHT',            type: 'number' },
  { key: 'rate',                    label: 'RATE',                    type: 'number' },
  { key: 'frt_per_lb',              label: 'FRT PER LB',              type: 'number' },
  { key: 'miles',                   label: 'MILES',                   type: 'number' },
  { key: 'invoice_no',              label: 'INVOICE NO',              type: 'text' },
  { key: 'invoice_paid_date',       label: 'INVOICE PAID DATE',       type: 'text' },
  { key: 'invoice_given_to_sam',    label: 'INVOICE GIVEN TO SAM',    type: 'text' },
  { key: 'frt_invoice',             label: 'FRT INVOICE',             type: 'text' },
  { key: 'senders_signature',       label: 'SENDERS SIGNATURE',       type: 'text' },
  { key: 'client_email',            label: 'CLIENT EMAIL',            type: 'email' },
];

function fmt(val) {
  if (val === null || val === undefined || val === '') return '—';
  return val;
}

// ── Tracking Page ─────────────────────────────────────────────
function TrackingPage({ record, onBack }) {
  const steps = [
    { icon: '📦', label: 'PICK UP DATE',           value: fmt(record.pick_up_date),           done: !!record.pick_up_date },
    { icon: '📍', label: 'PICKUP LOCATION',         value: fmt(record.pickup_location),         done: !!record.pickup_location },
    { icon: '🚛', label: 'TRUCKER',                 value: fmt(record.trucker),                 done: !!record.trucker },
    { icon: '🏁', label: 'ESTIMATED DELIVERY DATE', value: fmt(record.estimated_delivery_date), done: !!record.delivery_date },
  ];
  return (
    <div className="app">
      <div className="bg-layer" />
      <header className="header">
        <div className="header-inner">
          <img src="/rm-logo.png" alt="RM Metals" className="logo-img" />
          <div className="header-text">
            <h1 className="site-title">Track Your Shipment</h1>
            <p className="site-subtitle">Release # {record.release_no}</p>
          </div>
        </div>
      </header>
      <main className="main">
        <button className="back-btn" onClick={onBack}>← Back to Record</button>
        <section className="tracking-section">
          <div className="tracking-header">
            <div className="tracking-release">RELEASE #{record.release_no}</div>
            <div className={`tracking-status ${record.delivery_date ? 'delivered' : 'in-transit'}`}>
              {record.delivery_date ? '✓ DELIVERED' : '⟳ IN TRANSIT'}
            </div>
          </div>
          <div className="tracking-timeline">
            {steps.map((step, i) => (
              <div className={`timeline-step ${step.done ? 'done' : ''}`} key={i}>
                <div className="timeline-left">
                  <div className="timeline-icon">{step.icon}</div>
                  {i < steps.length - 1 && <div className="timeline-line" />}
                </div>
                <div className="timeline-content">
                  <div className="timeline-label">{step.label}</div>
                  <div className="timeline-value">{step.value}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="tracking-destination">
            <div className="dest-label">DESTINATION</div>
            <div className="dest-value">{fmt(record.destination_location)}</div>
          </div>
        </section>
      </main>
      <footer className="footer">RM Metals — Division of RM Creations Inc. · {new Date().getFullYear()}</footer>
    </div>
  );
}

// ── Email Modal ───────────────────────────────────────────────
function EmailModal({ record, onClose, authToken }) {
  const authHeaders = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` });
  const [status,        setStatus]        = useState('idle');
  const [errMsg,        setErrMsg]        = useState('');
  const [clientEmail,   setClientEmail]   = useState(record.client_email || '');
  const [blindShipment, setBlindShipment] = useState(false);
  const [attachments,   setAttachments]   = useState([]);
  const [attachError,   setAttachError]   = useState('');
  const fileInputRef = useRef(null);
  const MAX_TOTAL_MB = 15;

  const readAsBase64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload  = () => resolve(String(r.result).split(',')[1]);
    r.onerror = () => reject(new Error('Could not read ' + file.name));
    r.readAsDataURL(file);
  });

  const handleFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;
    setAttachError('');
    const total = [...attachments, ...picked].reduce((sum, f) => sum + f.size, 0);
    if (total > MAX_TOTAL_MB * 1024 * 1024) {
      setAttachError(`Attachments can't exceed ${MAX_TOTAL_MB} MB in total.`);
      return;
    }
    try {
      const added = await Promise.all(picked.map(async f => ({
        filename:    f.name,
        contentType: f.type || 'application/octet-stream',
        size:        f.size,
        content:     await readAsBase64(f),
      })));
      setAttachments(prev => [...prev, ...added]);
    } catch (err) {
      setAttachError(err.message || 'Could not read the selected file.');
    }
  };

  const removeAttachment = (i) => setAttachments(prev => prev.filter((_, idx) => idx !== i));

  const fmtSize = (bytes) => bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(2)} MB`;

  const handleSend = async () => {
    if (!clientEmail.trim()) { setErrMsg('Please enter a client email address.'); return; }
    setStatus('sending'); setErrMsg('');
    try {
      const res  = await fetch(`${API_BASE}/trucking/send-email`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          record, clientEmail: clientEmail.trim(), blindShipment,
          attachments: attachments.map(({ filename, contentType, content }) => ({ filename, contentType, content })),
        }),
      });
      const data = await res.json();
      if (!res.ok) { setStatus('error'); setErrMsg(data.error || 'Failed to send email.'); }
      else         { setStatus('success'); }
    } catch { setStatus('error'); setErrMsg('Could not reach the server.'); }
  };

  return (
    <div className="modal-overlay" onClick={status === 'sending' ? null : onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">✉ Email Shipping Info</div>
          {status !== 'sending' && <button className="modal-close" onClick={onClose}>✕</button>}
        </div>
        <div className="modal-body">

          {/* Client Email Input */}
          <div className="modal-field">
            <div className="modal-label">CLIENT EMAIL *</div>
            <input
              type="email"
              className="form-input"
              placeholder="Enter client email address"
              value={clientEmail}
              onChange={e => setClientEmail(e.target.value)}
              disabled={status === 'sending' || status === 'success'}
            />
          </div>

          {/* Blind Shipment Checkbox */}
          <div className="blind-shipment-row">
            <label className="blind-shipment-label">
              <input
                type="checkbox"
                checked={blindShipment}
                onChange={e => setBlindShipment(e.target.checked)}
                disabled={status === 'sending' || status === 'success'}
                className="blind-shipment-checkbox"
              />
              <span className="blind-shipment-text">Blind Shipment</span>
              <span className="blind-shipment-hint">
                {blindShipment ? '🔒 Pickup & destination details will be hidden from client' : 'All shipping details will be visible to client'}
              </span>
            </label>
          </div>

          {/* Subject */}
          <div className="modal-field">
            <div className="modal-label">SUBJECT</div>
            <div className="modal-value">Shipping info for release # {record.release_no}</div>
          </div>

          {/* Email Content Preview */}
          <div className="modal-field">
            <div className="modal-label">EMAIL CONTENT PREVIEW</div>
            <div className="modal-preview">
              <div className="preview-row"><span>Release #</span><span>{fmt(record.release_no)}</span></div>
              <div className="preview-row"><span>SO / CS No</span><span>{fmt(record.so_cs_no)}</span></div>
              <div className="preview-row"><span>PO No</span><span>{fmt(record.po_no)}</span></div>
              <div className={`preview-row ${blindShipment ? 'preview-masked' : ''}`}>
                <span>Pickup Location</span>
                <span>{blindShipment ? '🔒 Hidden' : fmt(record.pickup_location)}</span>
              </div>
              <div className={`preview-row ${blindShipment ? 'preview-masked' : ''}`}>
                <span>Destination</span>
                <span>{blindShipment ? '🔒 Hidden' : fmt(record.destination_location)}</span>
              </div>
              <div className="preview-row"><span>Scheduled Date</span><span>{fmt(record.scheduled_date)}</span></div>
              <div className="preview-row"><span>PO Due Date</span><span>{fmt(record.po_due_date)}</span></div>
              <div className="preview-row"><span>Est. Delivery Date</span><span>{fmt(record.estimated_delivery_date)}</span></div>
              <div className="preview-row"><span>Delivery Date</span><span>{fmt(record.delivery_date)}</span></div>
              <div className="preview-row"><span>Total Coils</span><span>{fmt(record.total_coils)}</span></div>
              <div className="preview-row"><span>Total Weight</span><span>{fmt(record.total_weight)}</span></div>
              <div className="preview-link-row"><span>🔗 Tracking link will be included</span></div>
            </div>
          </div>

          {/* Attachments */}
          {attachments.length > 0 && (
            <div className="modal-field">
              <div className="modal-label">ATTACHMENTS ({attachments.length})</div>
              <div className="attach-list">
                {attachments.map((a, i) => (
                  <div className="attach-chip" key={`${a.filename}-${i}`}>
                    <span className="attach-name" title={a.filename}>📎 {a.filename}</span>
                    <span className="attach-size">{fmtSize(a.size)}</span>
                    {status !== 'sending' && status !== 'success' && (
                      <button
                        type="button"
                        className="attach-remove"
                        title="Remove attachment"
                        onClick={() => removeAttachment(i)}
                      >✕</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {attachError && <div className="message error-msg"><span className="msg-icon">⚠</span> {attachError}</div>}

          {errMsg    && <div className="message error-msg"><span className="msg-icon">⚠</span> {errMsg}</div>}
          {status === 'success' && <div className="message success-msg"><span className="msg-icon">✓</span> Email sent to {clientEmail}</div>}
        </div>
        <div className="modal-footer">
          {status === 'success' ? (
            <button className="edit-btn" onClick={onClose}>Close</button>
          ) : (
            <>
              <input
                type="file"
                ref={fileInputRef}
                multiple
                hidden
                onChange={handleFiles}
              />
              <button
                type="button"
                className="attach-btn"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={status === 'sending'}
              >📎 Attach Files</button>
              <button className="cancel-btn" onClick={onClose} disabled={status === 'sending'}>Cancel</button>
              <button
                className={`send-email-btn ${!clientEmail.trim() || status === 'sending' ? 'disabled' : ''}`}
                onClick={handleSend}
                disabled={!clientEmail.trim() || status === 'sending'}
              >
                {status === 'sending' ? <><span className="spinner-dark" /> Sending…</> : '✉ Send Email'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Sync Modal ────────────────────────────────────────────────
function SyncModal({ onClose, authToken }) {
  const authHeaders = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` });
  const [status,  setStatus]  = useState('idle'); // idle | syncing | done | error
  const [message, setMessage] = useState('');
  const [stats,   setStats]   = useState(null);

  const handleSync = async () => {
    setStatus('syncing'); setMessage(''); setStats(null);
    try {
      const res  = await fetch(`${API_BASE}/trucking/sync-from-sheet`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) { setStatus('error'); setMessage(data.error || 'Sync failed.'); }
      else {
        setStatus('done');
        setStats(data);
        setMessage(data.message || 'Sync complete!');
      }
    } catch { setStatus('error'); setMessage('Could not reach the server.'); }
  };

  return (
    <div className="modal-overlay" onClick={status === 'syncing' ? null : onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">🔄 Sync from Google Sheet</div>
          {status !== 'syncing' && <button className="modal-close" onClick={onClose}>✕</button>}
        </div>
        <div className="modal-body">

          {/* Idle state — show info */}
          {status === 'idle' && (
            <>
              <div className="sync-info-box">
                <div className="sync-info-title">What this does:</div>
                <div className="sync-info-item">✅ Reads all rows from your Google Sheet</div>
                <div className="sync-info-item">✅ Inserts any new rows into the database</div>
                <div className="sync-info-item">✅ Updates existing rows with latest Sheet data</div>
                <div className="sync-info-item">✅ New records become searchable immediately</div>
              </div>
              <div className="sync-note">
                ⚠ Google Sheet is the source of truth — Sheet data will overwrite database data.
              </div>
            </>
          )}

          {/* Syncing state */}
          {status === 'syncing' && (
            <div className="sync-progress">
              <div className="sync-spinner-large" />
              <div className="sync-progress-text">Syncing from Google Sheet...</div>
              <div className="sync-progress-sub">This may take a few seconds</div>
            </div>
          )}

          {/* Done state */}
          {status === 'done' && (
            <div className="sync-result success">
              <div className="sync-result-icon">✓</div>
              <div className="sync-result-title">Sync Complete!</div>
              <div className="sync-result-message">{message}</div>
              {stats && (
                <div className="sync-stats">
                  <div className="sync-stat"><span>{stats.upserted || 0}</span><label>Records Synced</label></div>
                  <div className="sync-stat"><span>{stats.newRows  || 0}</span><label>New Rows Added</label></div>
                  <div className="sync-stat"><span>{stats.failed   || 0}</span><label>Failed</label></div>
                </div>
              )}
            </div>
          )}

          {/* Error state */}
          {status === 'error' && (
            <div className="sync-result error">
              <div className="sync-result-icon">✗</div>
              <div className="sync-result-title">Sync Failed</div>
              <div className="sync-result-message">{message}</div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {status === 'idle' && (
            <>
              <button className="cancel-btn" onClick={onClose}>Cancel</button>
              <button className="save-btn" onClick={handleSync}>🔄 Start Sync</button>
            </>
          )}
          {status === 'syncing' && (
            <button className="cancel-btn" disabled>Please wait…</button>
          )}
          {(status === 'done' || status === 'error') && (
            <>
              {status === 'error' && <button className="save-btn" onClick={handleSync}>↺ Retry</button>}
              <button className="edit-btn" onClick={onClose}>Close</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Change Password Modal ─────────────────────────────────────
function ChangePasswordModal({ onClose, authToken }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status,          setStatus]          = useState('idle');
  const [error,           setError]           = useState('');
  const [showCurrent,     setShowCurrent]     = useState(false);
  const [showNew,         setShowNew]         = useState(false);
  const [showConfirm,     setShowConfirm]     = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required.'); return;
    }
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters.'); return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.'); return;
    }
    if (currentPassword === newPassword) {
      setError('New password must be different from current password.'); return;
    }

    setStatus('loading');
    try {
      const res  = await fetch(`${API_BASE}/auth/change-password`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body:    JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to change password.'); setStatus('idle'); }
      else         { setStatus('success'); }
    } catch { setError('Could not connect to server.'); setStatus('idle'); }
  };

  return (
    <div className="modal-overlay" onClick={status === 'loading' ? null : onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">🔑 Change Password</div>
          {status !== 'loading' && <button className="modal-close" onClick={onClose}>✕</button>}
        </div>

        {status === 'success' ? (
          <>
            <div className="modal-body">
              <div className="change-pass-success">
                <div className="change-pass-success-icon">✓</div>
                <div className="change-pass-success-title">Password Changed!</div>
                <div className="change-pass-success-msg">Your password has been updated successfully.</div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="save-btn" onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="modal-field">
                <div className="modal-label">CURRENT PASSWORD</div>
                <div className="login-password-row">
                  <input
                    type={showCurrent ? 'text' : 'password'}
                    className="login-input"
                    placeholder="Enter current password"
                    value={currentPassword}
                    onChange={e => setCurrentPassword(e.target.value)}
                    disabled={status === 'loading'}
                    autoFocus
                  />
                  <button type="button" className="login-show-pass" onClick={() => setShowCurrent(!showCurrent)} tabIndex={-1}>
                    {showCurrent ? '🙈' : '👁'}
                  </button>
                </div>
              </div>

              <div className="modal-field">
                <div className="modal-label">NEW PASSWORD</div>
                <div className="login-password-row">
                  <input
                    type={showNew ? 'text' : 'password'}
                    className="login-input"
                    placeholder="Enter new password (min 6 characters)"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    disabled={status === 'loading'}
                  />
                  <button type="button" className="login-show-pass" onClick={() => setShowNew(!showNew)} tabIndex={-1}>
                    {showNew ? '🙈' : '👁'}
                  </button>
                </div>
              </div>

              <div className="modal-field">
                <div className="modal-label">CONFIRM NEW PASSWORD</div>
                <div className="login-password-row">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    className="login-input"
                    placeholder="Re-enter new password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    disabled={status === 'loading'}
                  />
                  <button type="button" className="login-show-pass" onClick={() => setShowConfirm(!showConfirm)} tabIndex={-1}>
                    {showConfirm ? '🙈' : '👁'}
                  </button>
                </div>
              </div>

              {/* Password strength indicator */}
              {newPassword && (
                <div className="pass-strength">
                  <div className="pass-strength-label">PASSWORD STRENGTH</div>
                  <div className="pass-strength-bar">
                    <div className={`pass-strength-fill ${
                      newPassword.length < 6 ? 'weak' :
                      newPassword.length < 10 ? 'medium' : 'strong'
                    }`} />
                  </div>
                  <div className="pass-strength-text">
                    {newPassword.length < 6 ? '⚠ Too short' :
                     newPassword.length < 10 ? '⚡ Medium' : '✓ Strong'}
                  </div>
                </div>
              )}

              {error && <div className="message error-msg"><span className="msg-icon">⚠</span> {error}</div>}
            </div>

            <div className="modal-footer">
              <button type="button" className="cancel-btn" onClick={onClose} disabled={status === 'loading'}>Cancel</button>
              <button type="submit" className="save-btn" disabled={status === 'loading'}>
                {status === 'loading' ? <><span className="spinner" /> Updating…</> : '🔑 Update Password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────
export default function App() {
  const [authUser,      setAuthUser]      = useState(() => {
    const u = localStorage.getItem('rm_user');
    return u ? JSON.parse(u) : null;
  });
  const [authToken,     setAuthToken]     = useState(() => localStorage.getItem('rm_token') || '');

  const handleLogin = (user, token) => {
    setAuthUser(user);
    setAuthToken(token);
  };

  const handleLogout = () => {
    localStorage.removeItem('rm_token');
    localStorage.removeItem('rm_user');
    setAuthUser(null);
    setAuthToken('');
  };

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${authToken}`,
  });

  const [query,         setQuery]         = useState('');
  const [record,        setRecord]        = useState(null);
  const [error,         setError]         = useState('');
  const [loading,       setLoading]       = useState(false);
  const [isEditing,     setIsEditing]     = useState(false);
  const [editForm,      setEditForm]      = useState({});
  const [saveSuccess,   setSaveSuccess]   = useState(false);
  const [isTracking,    setIsTracking]    = useState(false);
  const [showEmail,     setShowEmail]     = useState(false);
  const [showSync,      setShowSync]      = useState(false);
  const [showChangePass,setShowChangePass]= useState(false);
  const [showUserMenu,  setShowUserMenu]   = useState(false);
  const [showPDFCompare,setShowPDFCompare]= useState(false);

  useEffect(() => {
    const handleClick = (e) => {
      if (!e.target.closest('.user-menu-wrap')) setShowUserMenu(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true); setError(''); setRecord(null);
    setIsEditing(false); setSaveSuccess(false); setIsTracking(false); setShowEmail(false);
    try {
      const res  = await fetch(`${API_BASE}/trucking/search?release_no=${encodeURIComponent(query.trim())}`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (res.status === 401) { handleLogout(); return; }
      if (!res.ok) setError(data.error || 'No record found.');
      else setRecord(data);
    } catch { setError('Could not connect to server.'); }
    finally { setLoading(false); }
  };

  const handleEdit = () => {
    const form = {};
    FIELDS.forEach(f => { form[f.key] = record[f.key] !== null && record[f.key] !== undefined ? String(record[f.key]) : ''; });
    setEditForm(form); setIsEditing(true); setSaveSuccess(false);
  };

  const handleChange = (e) => setEditForm({ ...editForm, [e.target.name]: e.target.value });

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true); setSaveSuccess(false);
    try {
      const res  = await fetch(`${API_BASE}/trucking/${record.release_no}`, {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Failed to save.');
      else { setRecord(data); setIsEditing(false); setSaveSuccess(true); }
    } catch { setError('Could not connect to server.'); }
    finally { setLoading(false); }
  };

  // Show login page if not authenticated
  if (!authUser || !authToken) return <LoginPage onLogin={handleLogin} />;

  if (isTracking && record) return <TrackingPage record={record} onBack={() => setIsTracking(false)} />;
  if (showPDFCompare)       return <PDFCompare onBack={() => setShowPDFCompare(false)} />;

  return (
    <div className="app">
      <div className="bg-layer" />
      {showEmail && record && <EmailModal record={record} onClose={() => setShowEmail(false)} authToken={authToken} />}
      {showSync  && <SyncModal onClose={() => setShowSync(false)} authToken={authToken} />}
      {showChangePass && <ChangePasswordModal onClose={() => setShowChangePass(false)} authToken={authToken} />}

      <header className="header">
        <div className="header-inner">
          <img src="/rm-logo.png" alt="RM Metals" className="logo-img" />
          <div className="header-text">
            <h1 className="site-title">Welcome to RM Record Search</h1>
            <p className="site-subtitle">RM Trucking Database</p>
          </div>
          <div className="header-gap" />
          <div className="header-actions">
            <button className="sync-btn" onClick={() => setShowSync(true)}>🔄 Sync Sheet</button>
            <button className="pdf-compare-link" onClick={() => setShowPDFCompare(true)}>📋 Compare PDFs</button>
            <div className="hdr-divider-v" />
            <div className="user-menu-wrap">
              <button className="user-menu-btn" onClick={() => setShowUserMenu(m => !m)}>
                <div className="user-avatar">{(authUser?.full_name || authUser?.username || 'U')[0].toUpperCase()}</div>
                <span className="user-menu-name">{authUser?.full_name || authUser?.username}</span>
                <span className={`user-menu-chev ${showUserMenu ? 'open' : ''}`}>▼</span>
              </button>
              {showUserMenu && (
                <div className="user-dropdown">
                  <div className="user-dd-header">
                    <div className="user-dd-name">{authUser?.full_name || authUser?.username}</div>
                    <div className="user-dd-role">{authUser?.role || 'user'}</div>
                  </div>
                  <button className="user-dd-item" onClick={() => { setShowChangePass(true); setShowUserMenu(false); }}>
                    🔑 Change Password
                  </button>
                  <div className="user-dd-sep" />
                  <button className="user-dd-item danger" onClick={() => { handleLogout(); setShowUserMenu(false); }}>
                    → Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="main">
        <section className="search-section">
          <form onSubmit={handleSearch} className="search-form">
            <label className="search-label">RELEASE # LOOKUP</label>
            <div className="search-row">
              <input
                type="number" className="search-input"
                placeholder="Enter a RELEASE # to search"
                value={query} onChange={e => setQuery(e.target.value)} autoFocus
              />
              <button type="submit" className="search-btn" disabled={loading}>
                {loading ? <span className="spinner" /> : 'SEARCH'}
              </button>
            </div>
          </form>
        </section>

        {error       && <div className="message error-msg"><span className="msg-icon">⚠</span> {error}</div>}
        {saveSuccess && <div className="message success-msg"><span className="msg-icon">✓</span> Record updated successfully.</div>}

        {record && !isEditing && (
          <section className="result-section">
            <div className="result-header">
              <div className="result-badge">RECORD FOUND — RELEASE #{record.release_no}</div>
              <div className="result-actions">
                <button className="email-btn" onClick={() => setShowEmail(true)}>✉ Email Shipping Info</button>
                <button className="track-btn" onClick={() => setIsTracking(true)}>🚛 Track Your Shipment</button>
                <button className="edit-btn"  onClick={handleEdit}>✎ Edit Record</button>
              </div>
            </div>
            <div className="record-grid">
              {FIELDS.map(f => (
                <div className="record-field" key={f.key}>
                  <span className="field-label">{f.label}</span>
                  <span className={`field-value${f.key === 'release_no' ? ' highlight' : ''}`}>
                    {f.key === 'client_email' && record[f.key]
                      ? <a href={`mailto:${record[f.key]}`} className="email-link">{record[f.key]}</a>
                      : fmt(record[f.key])}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {isEditing && (
          <section className="edit-section">
            <div className="edit-header">
              <div className="result-badge edit-badge">EDIT — RELEASE #{record.release_no}</div>
              <button className="cancel-btn" onClick={() => setIsEditing(false)}>✕ Cancel</button>
            </div>
            <form onSubmit={handleSave} className="edit-form">
              <div className="form-grid">
                {FIELDS.map(f => (
                  <div className={`form-group${f.type === 'textarea' ? ' full-width' : ''}`} key={f.key}>
                    <label className="form-label">
                      {f.label}{f.readonly ? ' (read-only)' : ''}
                      {['date','po_due_date','pick_up_date','scheduled_date','estimated_delivery_date',
                        'delivery_date','invoice_paid_date','invoice_given_to_sam'].includes(f.key)
                        ? ' (MM/DD/YYYY)' : ''}
                    </label>
                    {f.type === 'textarea' ? (
                      <textarea name={f.key} className="form-input form-textarea"
                        value={editForm[f.key] || ''} onChange={handleChange} rows={3} />
                    ) : (
                      <input
                        type={f.type === 'email' ? 'email' : f.type === 'number' && !f.readonly ? 'number' : 'text'}
                        name={f.key}
                        className={`form-input${f.readonly ? ' readonly' : ''}`}
                        value={editForm[f.key] || ''}
                        onChange={handleChange}
                        readOnly={f.readonly}
                        placeholder={['date','po_due_date','pick_up_date','scheduled_date',
                          'estimated_delivery_date','delivery_date','invoice_paid_date',
                          'invoice_given_to_sam'].includes(f.key) ? 'MM/DD/YYYY' : ''}
                      />
                    )}
                  </div>
                ))}
              </div>
              <button type="submit" className="save-btn" disabled={loading}>
                {loading ? <><span className="spinner" /> Saving…</> : '✓ Save Changes'}
              </button>
            </form>
          </section>
        )}
      </main>
      <footer className="footer">RM Metals — Division of RM Creations Inc. · {new Date().getFullYear()}</footer>
    </div>
  );
}
